<template>
  <div class="distortion-report-detail-container">
    <!-- 1. 顶部图像预览 -->
    <div class="report-header-images pdf-atomic-block">
      <div class="labeled-image-container">
        <div class="image-label-wrapper">
          <el-tag type="primary" effect="dark">{{
            $t("report.originalImage")
          }}</el-tag>
        </div>
        <div class="image-wrapper">
          <div v-if="convertingMap.original" class="image-loading">
            <i class="el-icon-loading"></i>
          </div>
          <div v-else-if="conversionErrors.original" class="image-error">
            {{ $t("error.operationFailed") }}
          </div>
          <img
            v-else
            :src="
              getDisplayUrl(
                'original',
                reportResultData && reportResultData.image_url
              )
            "
            crossorigin="anonymous"
            alt="Original Image Preview"
          />
        </div>
      </div>
      <div class="labeled-image-container">
        <div class="image-label-wrapper">
          <el-tag type="primary" effect="dark">{{
            $t("report.roiImage")
          }}</el-tag>
        </div>
        <div class="image-wrapper">
          <div v-if="convertingMap.roi" class="image-loading">
            <i class="el-icon-loading"></i>
          </div>
          <div v-else-if="conversionErrors.roi" class="image-error">
            {{ $t("error.operationFailed") }}
          </div>
          <img
            v-else
            :src="
              getDisplayUrl(
                'roi',
                reportResultData && reportResultData.image_roi_url
              )
            "
            crossorigin="anonymous"
            alt="ROI Image"
          />
        </div>
      </div>
    </div>

    <div class="pdf-atomic-block">
      <!-- 2. 分析结果 -->
      <h4 id="analysis-result" class="section-title">
        {{ $t("report.analysis_result_title") }}
      </h4>
      <el-table
        :data="reportResultData.result.table_data_form"
        style="max-width: 600px; margin: 20px auto"
        border
        class="custom-table"
      >
        <el-table-column prop="item" label="Item"></el-table-column>
        <el-table-column prop="data" label="Value"></el-table-column>
      </el-table>
    </div>

    <!-- 3. 亮度分布 -->
    <div class="pdf-atomic-block">
      <h4 id="brightness-distribution" class="section-title">
        {{ $t("report.Luminance_distribution_title") }}
      </h4>
      <h5 class="sub-section-title">{{ $t("report.Analysis_Chart") }}</h5>
      <div class="chart-container">
        <div
          v-if="conversionErrors.roi"
          class="image-error analysis-placeholder"
        >
          {{ $t("error.operationFailed") }}
        </div>
        <div v-else-if="convertingMap.roi" class="analysis-placeholder">
          <i class="el-icon-loading"></i>
        </div>
        <img
          v-else
          class="analysis-image"
          :src="
            getDisplayUrl(
              'roi',
              reportResultData && reportResultData.image_roi_url
            )
          "
          crossorigin="anonymous"
          alt="Distortion Analysis Image"
        />
      </div>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="section-main-title">{{ $t("report.result_chart") }}</h5>
      <h5 class="sub-section-title">{{ $t("report.LCA_chart") }}</h5>
      <div class="chart-container">
        <div class="chart-inner-wrapper">
          <v-chart
            class="echart"
            :option="lcaChartOption"
            ref="lcaChartOption_Chart"
            autoresize
          />
        </div>
        <div class="result-block">
          <p>
            <span style="font-weight: bold">{{ $t("report.result") }}</span
            >{{ reportResultData.result.lac_graph.result_text }}
          </p>
        </div>
      </div>
    </div>

    <div class="pdf-atomic-block">
      <h5 class="sub-section-title">{{ $t("report.Distortion_chart") }}</h5>
      <div class="chart-container">
        <div class="chart-inner-wrapper">
          <v-chart
            class="echart"
            :option="distortionChartOption"
            ref="distortionChartOption_Chart"
            autoresize
          />
        </div>
        <div class="result-block">
          <p>
            <span style="font-weight: bold">{{ $t("report.result") }}</span
            >{{ reportResultData.result.distortion_graph.result_text }}
          </p>
        </div>
      </div>
    </div>

    <!-- ** 回到顶部按钮 ** -->
    <el-backtop
      v-if="!isPrinting"
      :target="backtopTarget"
      :right="40"
      :bottom="40"
    />
  </div>
</template>

<script>
import { backtopMixin } from "@/mixins/backtopMixin";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart, ScatterChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
} from "echarts/components";
import VChart from "vue-echarts";
import UTIF from "utif";

use([
  CanvasRenderer,
  LineChart,
  ScatterChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
]);

export default {
  name: "DistortionReportDetail",
  mixins: [backtopMixin],
  components: { VChart },
  props: {
    reportData: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      isMobile: window.innerWidth <= 768,
      isPrinting: false,
      convertedImageUrls: {
        original: null,
        roi: null,
      },
      convertingMap: {
        original: false,
        roi: false,
      },
      conversionErrors: {},
    };
  },
  computed: {
    reportResultData() {
      if (this.reportData) {
        return this.reportData;
      }
      return null;
    },
    lcaChartOption() {
      if (!this.reportResultData) return {};
      const chartData = this.reportResultData.result.lac_graph;
      const seriesData = chartData.series;

      const seriesConfig = [
        {
          displayName: "Red",
          scatterName: "Red (+)",
          color: "#d9534f",
        },
        {
          displayName: "Blue",
          scatterName: "Blue (+)",
          color: "#0275d8",
        },
        { displayName: "Green", scatterName: "Green (+)", color: "#5cb85c" },
      ];

      const series = seriesData.flatMap((s, index) => {
        const config = seriesConfig[index];
        const lineData = this.generateCurveData(
          s.fit_curve,
          chartData.axis_ranges.xAxisTop
        );
        const scatterData = s.scatter;

        const lineSeries = {
          name: config.displayName,
          type: "line",
          smooth: true,
          color: config.color,
          symbol: "none",
          lineStyle: { type: "dashed" },
          xAxisIndex: 1,
          data: lineData,
        };

        const scatterSeries = {
          name: config.scatterName,
          type: "scatter",
          symbol: "path://M-3,0 L3,0 M0,-3 L0,3",
          symbolSize: 6,
          itemStyle: {
            color: config.color,
            borderColor: config.color,
            borderWidth: 1.5,
          },
          data: scatterData,
        };

        return [lineSeries, scatterSeries];
      });

      const legendData = seriesConfig.flatMap((config) => {
        const lineLegend = config.displayName;
        const scatterLegend = {
          name: config.scatterName,
          icon: "path://M-3,0 L3,0 M0,-3 L0,3",
        };
        return [lineLegend, scatterLegend];
      });

      return {
        grid: {
          show: true,
          borderColor: "black",
          top: 60,
          bottom: this.isMobile ? 160 : 140,
          left: 90,
          right: 90,
        },
        tooltip: { trigger: "axis" },
        legend: {
          type: "scroll",
          orient: "horizontal",
          left: "center",
          data: legendData,
          bottom: 10,
          itemGap: this.isMobile ? 12 : 24,
          textStyle: {
            fontSize: this.isMobile ? 10 : 12,
            lineHeight: this.isMobile ? 14 : 18,
          },
          pageButtonItemGap: 5,
          pageIconSize: 10,
        },
        xAxis: [
          {
            type: "value",
            min: chartData.axis_ranges.xAxisBottom[0],
            max: chartData.axis_ranges.xAxisBottom[1],
            position: "bottom",
            name: "Image Radius in Pixels",
            nameLocation: "middle",
            nameGap: 35,
            splitLine: { show: true },
            axisLine: { show: false },
            axisLabel: { show: true },
            axisTick: { show: false },
          },
          {
            type: "value",
            min: chartData.axis_ranges.xAxisTop[0],
            max: chartData.axis_ranges.xAxisTop[1],
            position: "top",
            name: "Normalized Image Radius",
            nameLocation: "middle",
            nameGap: 35,
            splitLine: { show: false },
            axisLine: { show: false },
            axisLabel: { show: true },
            axisTick: { show: false },
          },
        ],
        yAxis: [
          {
            type: "value",
            min: chartData.axis_ranges.yAxisLeft[0],
            max: chartData.axis_ranges.yAxisLeft[1],
            position: "left",
            name: "LCA in Pixels",
            nameLocation: "middle",
            nameGap: 60,
            splitLine: { show: true },
            axisLine: { show: false },
            axisLabel: { show: true },
            axisTick: { show: false },
          },
          {
            type: "value",
            min: chartData.axis_ranges.yAxisRight[0],
            max: chartData.axis_ranges.yAxisRight[1],
            position: "right",
            name: "LCA in Image Height Percentage",
            nameLocation: "middle",
            nameGap: 60,
            splitLine: { show: false },
            axisLine: { show: false },
            axisLabel: { show: true },
            axisTick: { show: false },
          },
        ],
        series: series,
      };
    },
    distortionChartOption() {
      if (!this.reportResultData) return {};
      const { distortion_graph: chartData } = this.reportResultData.result;
      const { series: seriesData, axis_ranges } = chartData;

      const seriesConfig = [
        {
          scatterName: "Optical Distortion Data",
          lineName: "Optical Distortion LSQ fit",
          color: "#c23531",
        },
        {
          scatterName: "SMIA TV Distortion Data",
          lineName: "SMIA TV Distortion from LSQ fit",
          color: "#52c41a",
        },
      ];

      const series = seriesData.flatMap((s, index) => {
        const config = seriesConfig[index];

        // 生成拟合曲线
        const lineData = this.generateCurveData(
          s.fit_curve,
          axis_ranges.xAxisTop
        );

        const scatterSeries = {
          name: config.scatterName,
          type: "scatter",
          symbol: "path://M-3,0 L3,0 M0,-3 L0,3",
          symbolSize: 6,
          itemStyle: {
            color: config.color,
            borderColor: config.color,
            borderWidth: 1.5,
          },
          data: s.scatter,
        };

        // 将其散点数据关联到顶部的 x 轴 (xAxisIndex: 1)
        if (index === 1) {
          scatterSeries.xAxisIndex = 1;
        }

        const lineSeries = {
          name: config.lineName,
          type: "line",
          symbol: "none",
          color: config.color,
          lineStyle: { type: "dashed" },
          xAxisIndex: 1,
          data: lineData,
        };

        return [scatterSeries, lineSeries];
      });

      const legendData = seriesConfig.flatMap((config) => [
        config.scatterName,
        config.lineName,
      ]);

      return {
        grid: {
          show: true,
          borderColor: "black",
          top: 60,
          bottom: this.isMobile ? 180 : 150,
          left: 90,
          right: 90,
        },
        tooltip: { trigger: "axis" },
        legend: {
          type: "scroll",
          orient: "horizontal",
          left: "center",
          data: legendData,
          bottom: 10,
          itemGap: this.isMobile ? 12 : 24,
          textStyle: {
            fontSize: this.isMobile ? 10 : 12,
            lineHeight: this.isMobile ? 14 : 18,
          },
          pageButtonItemGap: 5,
          pageIconSize: 10,
        },
        xAxis: [
          {
            type: "value",
            min: axis_ranges.xAxis[0],
            max: axis_ranges.xAxis[1],
            position: "bottom",
            name: "Image Radius in Pixels",
            nameLocation: "middle",
            nameGap: 35,
            splitLine: { show: true },
            axisLine: { show: false },
            axisLabel: { show: true },
            axisTick: { show: false },
          },
          {
            type: "value",
            min: axis_ranges.xAxisTop[0],
            max: axis_ranges.xAxisTop[1],
            position: "top",
            name: "Normalized Image Radius",
            nameLocation: "middle",
            nameGap: 35,
            splitLine: { show: true },
            axisLine: { show: false },
            axisLabel: { show: true },
            axisTick: { show: false },
          },
        ],
        yAxis: {
          type: "value",
          min: axis_ranges.yAxis[0],
          max: axis_ranges.yAxis[1],
          position: "left",
          name: "Distortion in %",
          nameLocation: "middle",
          nameGap: 60,
          splitLine: { show: false },
          axisLine: { show: false },
          axisLabel: { show: true },
          axisTick: { show: false },
        },
        series: series,
      };
    },
  },
  watch: {
    "reportData.image_url": {
      handler(newUrl) {
        this.handleImageSourceChange("original", newUrl);
      },
      immediate: true,
    },
    "reportData.image_roi_url": {
      handler(newUrl) {
        this.handleImageSourceChange("roi", newUrl);
      },
      immediate: true,
    },
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    isTifUrl(url) {
      if (!url) return false;
      const lower = String(url).toLowerCase();
      return lower.endsWith(".tif") || lower.endsWith(".tiff");
    },
    getDisplayUrl(type, url) {
      if (!url) return "";
      const converted = this.convertedImageUrls[type];
      if (converted) {
        return converted;
      }
      if (!this.isTifUrl(url)) {
        return url;
      }
      if (!this.convertingMap[type]) {
        this.convertTifImage(type, url);
      }
      return "";
    },
    handleImageSourceChange(type, url) {
      this.$delete(this.conversionErrors, type);
      this.$set(this.convertedImageUrls, type, null);
      if (!url) {
        this.$set(this.convertingMap, type, false);
        return;
      }
      if (this.isTifUrl(url)) {
        this.convertTifImage(type, url);
      } else {
        this.$set(this.convertingMap, type, false);
      }
    },
    async convertTifImage(type, url) {
      if (!url || this.convertingMap[type]) {
        return;
      }
      this.$set(this.convertingMap, type, true);
      this.$delete(this.conversionErrors, type);
      try {
        const isMobile = window.innerWidth <= 768;
        
        // 手机端增加超时时间
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), isMobile ? 30000 : 15000);
        
        const response = await fetch(url, {
          cache: "no-store",
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.statusText}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        const ifds = UTIF.decode(arrayBuffer);
        if (!ifds.length) {
          throw new Error("No image data found in TIF file");
        }
        const firstImage = ifds[0];
        UTIF.decodeImage(arrayBuffer, firstImage);
        let { width, height } = firstImage;
        if (!width || !height) {
          throw new Error("Invalid TIF image size");
        }
        
        // 手机端：检查并限制canvas大小，避免超出浏览器限制
        const maxCanvasSize = isMobile ? 2048 : 4096;
        let scale = 1;
        if (width > maxCanvasSize || height > maxCanvasSize) {
          scale = Math.min(maxCanvasSize / width, maxCanvasSize / height);
          width = Math.floor(width * scale);
          height = Math.floor(height * scale);
        }
        
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        
        if (!ctx) {
          throw new Error("无法创建canvas上下文");
        }
        
        // 如果图片太大需要缩放，直接缩放RGBA数据
        const imageData = ctx.createImageData(width, height);
        const rgba = UTIF.toRGBA8(firstImage);
        
        if (scale < 1) {
          const originalWidth = firstImage.width;
          const originalHeight = firstImage.height;
          const invScale = 1 / scale;
          
          for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
              const srcX = Math.min(Math.floor(x * invScale), originalWidth - 1);
              const srcY = Math.min(Math.floor(y * invScale), originalHeight - 1);
              const srcIdx = (srcY * originalWidth + srcX) * 4;
              const dstIdx = (y * width + x) * 4;
              imageData.data[dstIdx] = rgba[srcIdx];
              imageData.data[dstIdx + 1] = rgba[srcIdx + 1];
              imageData.data[dstIdx + 2] = rgba[srcIdx + 2];
              imageData.data[dstIdx + 3] = rgba[srcIdx + 3];
            }
          }
        } else {
          imageData.data.set(rgba);
        }
        
        ctx.putImageData(imageData, 0, 0);
        const dataUrl = canvas.toDataURL("image/png");
        
        // 清理临时数据
        canvas.width = 0;
        canvas.height = 0;
        
        // 使用$set确保响应式更新，并强制触发视图更新
        this.$set(this.convertedImageUrls, type, dataUrl);
        
        // 手机端：强制更新视图，确保图片显示
        const isMobileForUpdate = window.innerWidth <= 768;
        if (isMobileForUpdate) {
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
      } catch (error) {
        console.error(`Failed to convert TIF image (${type}):`, error);
        this.$set(
          this.conversionErrors,
          type,
          (error && error.message) || "convert failed"
        );
        this.$set(this.convertedImageUrls, type, null);
      } finally {
        this.$set(this.convertingMap, type, false);
      }
    },
    /**
     * 供父组件 ReportPanel.vue 调用
     * 切换打印状态。
     * @param {boolean} isPrinting - 是否处于打印/导出状态
     */
    togglePrintingState(isPrinting) {
      this.isPrinting = isPrinting;
    },
    getChartInstances() {
      const instances = [];
      if (this.$refs.lcaChartOption_Chart) {
        instances.push(this.$refs.lcaChartOption_Chart.chart);
      }
      if (this.$refs.distortionChartOption_Chart) {
        instances.push(this.$refs.distortionChartOption_Chart.chart);
      }
      return instances;
    },
    /**
     * 确保所有图表都已渲染完成。
     */
    ensureChartsRendered() {
      const chartInstances = this.getChartInstances();
      if (!chartInstances || chartInstances.length === 0) {
        return Promise.resolve();
      }
      const promises = chartInstances.map(
        (chart) =>
          new Promise((resolve) => {
            // 'finished' 事件确保渲染完成
            const listener = () => {
              resolve();
              chart.off("finished", listener);
            };
            chart.on("finished", listener);
            // 超时保护
            setTimeout(() => {
              resolve();
              chart.off("finished", listener);
            }, 2000);
          })
      );
      return Promise.all(promises);
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 768;
    },
    /**
     * @description: 根据拟合曲线的系数生成曲线数据
     * @param {object} fitCurve - 包含系数的对象
     * @param {Array} xRange - x轴的范围 [min, max]
     * @param {number} steps - 生成点的数量
     * @returns {Array} - 生成的曲线数据点 [[x1, y1], [x2, y2], ...]
     */
    generateCurveData(fitCurve, xRange, steps = 200) {
      if (!fitCurve || !fitCurve.coeffs || fitCurve.coeffs.length === 0) {
        return [];
      }

      const coeffs = fitCurve.coeffs;
      const [minX, maxX] = xRange;
      const points = [];
      const stepSize = (maxX - minX) / (steps - 1);

      // 根据系数数量判断是三次还是五次拟合
      if (coeffs.length === 4) {
        // 三次拟合: y = a*x^3 + b*x^2 + c*x + d
        // coeffs 顺序: [d, c, b, a]
        const [d, c, b, a] = coeffs;
        for (let i = 0; i < steps; i++) {
          const x = minX + i * stepSize;
          const y = a * Math.pow(x, 3) + b * Math.pow(x, 2) + c * x + d;
          points.push([x, y]);
        }
      } else if (coeffs.length === 6) {
        // 五次拟合: y = a*x^5 + b*x^4 + c*x^3 + d*x^2 + e*x + f
        // coeffs 顺序: [f, e, d, c, b, a]
        const [f, e, d, c, b, a] = coeffs;
        for (let i = 0; i < steps; i++) {
          const x = minX + i * stepSize;
          const y =
            a * Math.pow(x, 5) +
            b * Math.pow(x, 4) +
            c * Math.pow(x, 3) +
            d * Math.pow(x, 2) +
            e * x +
            f;
          points.push([x, y]);
        }
      }

      return points;
    },

    scrollToSection(sectionId) {
      const scrollContainer = this.$el.closest(
        ".detail-scrollbar .el-scrollbar__wrap"
      );
      if (!scrollContainer) return;

      if (sectionId === "top") {
        scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const targetElement = this.$el.querySelector(`#${sectionId}`);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    },
  },
};
</script>

<style scoped>
.custom-table ::v-deep .el-table__cell {
  padding: 8px 0;
}
.labeled-image-container {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.image-label-wrapper {
  padding: 0 5px;
  text-align: left;
}
.distortion-report-detail-container {
  padding-right: 15px;
}
.report-header-images {
  display: flex;
  gap: 20px;
  margin-bottom: 25px;
  justify-content: center;
}
.image-wrapper {
  border: 1px solid #e6ebf5;
  border-radius: 6px;
  padding: 20px;
  background-color: #fcfcfc;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 160px;
}
.image-wrapper img {
  width: 100%;
  height: auto;
  border-radius: 4px;
  display: block;
}
.image-loading,
.image-error {
  width: 100%;
  text-align: center;
  color: #909399;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.image-loading .el-icon-loading {
  font-size: 24px;
  color: #409eff;
}
.image-error {
  color: #f56c6c;
}
.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin-top: 35px;
  margin-bottom: 20px;
}
.section-main-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-top: 25px;
  margin-bottom: 15px;
}
.sub-section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-top: 25px;
  margin-bottom: 15px;
  padding-left: 12px;
  border-left: 4px solid #00a0e9;
}
.chart-container {
  width: 100%;
  margin-bottom: 20px;
  padding: 5px;
  border: 1px solid #e6ebf5;
  border-radius: 6px;
  box-sizing: border-box;
}
.analysis-image {
  width: 85%;
  height: auto;
  border-radius: 4px;
  display: block;
  max-width: 450px;
  margin: 30px auto;
}
.analysis-placeholder {
  width: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 30px auto;
  border-radius: 4px;
}
.analysis-placeholder .el-icon-loading {
  font-size: 26px;
  color: #409eff;
}
.chart-inner-wrapper {
  max-width: 100%;
  margin: 0 auto;
}
.chart-inner-wrapper.large {
  max-width: 100%;
}
.echart {
  height: 500px;
}
.custom-table ::v-deep th {
  background-color: #f5f7fa !important;
  color: #606266;
  font-weight: 600;
}
.custom-table ::v-deep td,
.custom-table ::v-deep th {
  text-align: center;
}
.result-block {
  background-color: #f5f7fa;
  padding: 15px 20px;
  border-radius: 4px;
  margin: 20px auto 0 auto;
  color: #303133;
  max-width: 100%;
  box-sizing: border-box;
}
.result-block p {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  word-break: break-word;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  line-height: 1.6;
  min-height: 1.6em;
}
@media (max-width: 768px) {
  .report-header-images {
    flex-direction: column;
  }
  .image-wrapper {
    padding: 15px;
  }
}
</style>
